import { computed, onMounted, onBeforeUnmount, ref, Ref } from 'vue';
import { normalObserve, normalUnobserve } from '@/observer';

/**
 * 关闭按钮显示控制器
 * 根据指定的顶部元素和底部元素位置控制关闭按钮的显示状态
 * @param topRef 顶部元素引用
 * @param bottomRef 底部元素引用
 * @returns 返回按钮隐藏状态
 */
function useCloseBtnVisibility(
    topRef: Ref<HTMLDivElement | null>,
    bottomRef: Ref<HTMLDivElement | null>,
) {
    // 按钮是否因为 "顶部元素" 超出视口下边界而隐藏
    const isHideByTop = ref(false);

    // 按钮是否因为 "底部元素" 进入视口而隐藏
    const isHideByBottom = ref(false);

    // 最终的按钮隐藏状态
    const isHide = computed(() => isHideByTop.value || isHideByBottom.value);

    // 处理 "顶部元素" 位置变化
    const handleTopElChange = (entry: IntersectionObserverEntry) => {
        const rect = entry.target.getBoundingClientRect();
        // "顶部元素" 超出视口下边界时隐藏按钮
        isHideByTop.value = Math.ceil(rect.top) > window.innerHeight;
    };

    // 处理 "底部元素" 位置变化
    const handleBottomElChange = (entry: IntersectionObserverEntry) => {
        const rect = entry.target.getBoundingClientRect();
        // "底部元素" 进入视口时隐藏按钮
        isHideByBottom.value = Math.ceil(rect.top) <= window.innerHeight;
    };

    // 开始监控元素位置
    const startObserving = () => {
        if (topRef.value) {
            normalObserve(topRef.value, {
                onExposure: handleTopElChange,
                onLoseExposure: handleTopElChange,
            });
        }

        if (bottomRef.value) {
            normalObserve(bottomRef.value, {
                onExposure: handleBottomElChange,
                onLoseExposure: handleBottomElChange,
            });
        }
    };

    // 停止监控元素位置
    const stopObserving = () => {
        if (topRef.value) normalUnobserve(topRef.value);
        if (bottomRef.value) normalUnobserve(bottomRef.value);
    };

    // 组件挂载时开始监控
    onMounted(startObserving);

    // 组件卸载时停止监控
    onBeforeUnmount(stopObserving);

    return {
        isHide: isHide,
    };
}

export default useCloseBtnVisibility;
